import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FaArrowLeft, FaPlus, FaTrash } from "react-icons/fa";
import { Button } from "../../../components/ui";
import apiService from "../../../services/apiService";
import Loader from "../../../components/ui/Loader";
import { useToast } from "../../../context/ToastContext";

const CHECK_CONVERSION_API = "/i/ims/tools/qSet/check-section-conversion";
const POLL_INTERVAL_MS = 10000;

const createUploadApi = (qSetId) => {
  return `/i/ims/tools/qSet/${qSetId}/upload-section-question-pdf`;
};

const makeSectionKeyFromName = (name, fallbackIndex) => {
  const cleanName = String(name || "")
    .trim()
    .toUpperCase()
    .replace(/&/g, "AND")
    .replace(/\+/g, "AND")
    .replace(/[^A-Z0-9\u0900-\u097F]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return cleanName || `SECTION_${fallbackIndex}`;
};

const getRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.records)) return response.records;
  return [];
};

const getDataObject = (response) => {
  return response?.data?.data || response?.data || response || {};
};

const isNoConversionStatus = (status) => {
  if (!status || typeof status !== "object") return true;

  const message = String(status.message || "")
    .trim()
    .toLowerCase();

  if (
    status.found === false ||
    message.includes("no question conversion found") ||
    message.includes("question conversion not found")
  ) {
    return true;
  }

  const hasJobId = Boolean(String(status.jobId || "").trim());
  const hasUploadStatus = Boolean(String(status.uploadStatus || "").trim());
  const isRunning = status.isRunning === true;

  return !hasJobId && !hasUploadStatus && !isRunning;
};

const getTerminalState = (status) => {
  const activeStep = Number(status?.activeStep ?? 0);
  const uploadStatus = String(status?.uploadStatus || "")
    .trim()
    .toLowerCase();

  const failed =
    activeStep < 0 || uploadStatus === "failed" || uploadStatus === "error";

  const completed =
    !failed &&
    (activeStep >= 3 ||
      uploadStatus === "completed" ||
      uploadStatus === "complete" ||
      uploadStatus === "uploaded");

  return {
    activeStep,
    uploadStatus,
    failed,
    completed,
    terminal: failed || completed,
  };
};

const shouldContinuePolling = (status) => {
  if (isNoConversionStatus(status)) return false;

  const terminalState = getTerminalState(status);
  if (terminalState.terminal) return false;

  const hasJobId = Boolean(String(status?.jobId || "").trim());

  return (
    status?.isRunning === true ||
    hasJobId ||
    terminalState.uploadStatus === "uploading" ||
    terminalState.uploadStatus === "processing" ||
    terminalState.uploadStatus === "pending" ||
    terminalState.uploadStatus === "running"
  );
};

const getSubjectName = (subject) => {
  return (
    subject?.name ||
    subject?.title ||
    subject?.value ||
    subject?.valueAlias ||
    subject?.code ||
    ""
  );
};

const getStageText = (activeStep) => {
  const step = Number(activeStep);

  if (step === -1) return "Conversion failed";
  if (step === 0) return "PDF conversion running";
  if (step === 1) return "PDF converted to text";
  if (step === 2) return "AI parsing running";
  if (step >= 3) return "Conversion completed";

  return "Conversion pending";
};

const getProgressPercent = (activeStep) => {
  const step = Number(activeStep);

  if (step < 0) return 100;
  if (step === 0) return 20;
  if (step === 1) return 45;
  if (step === 2) return 75;
  if (step >= 3) return 100;

  return 0;
};

const createEmptySubjectRow = () => ({
  subject: "",
  subjectId: "",
  subjectName: "",
  sectionKey: "",
  sectionName: "",
  questionStart: "",
  questionEnd: "",
});

const UploadQuestionNew = () => {
  const navigate = useNavigate();
  const { id: qSetId } = useParams();
  const pollTimerRef = useRef(null);
  const pollRequestInFlightRef = useRef(false);
  const terminalToastKeyRef = useRef("");
  const shouldShowProgressRef = useRef(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      layoutType: "AUTO",
      translateMissingLanguage: false,
    },
  });

  const { showSuccess, showError, showWarning, showInfo } = useToast();

  const [loading, setLoading] = useState(false);
  const [streams, setStreams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [conversionStatus, setConversionStatus] = useState(null);
  const [polling, setPolling] = useState(false);
  const [subjectRows, setSubjectRows] = useState([createEmptySubjectRow()]);

  const selectedStream = watch("stream");
  const selectedLayoutType = watch("layoutType") || "AUTO";
  const translateMissingLanguage = Boolean(watch("translateMissingLanguage"));
  const totalQuestions = Number(watch("totalQuestions") || 0);

  // useEffect(() => {
  //   loadStreams();

  //   return () => {
  //     if (pollTimerRef.current) {
  //       clearTimeout(pollTimerRef.current);
  //       pollTimerRef.current = null;
  //     }

  //     pollRequestInFlightRef.current = false;
  //   };
  // }, []);
  useEffect(() => {
    loadStreams();

    const resumeRunningConversion = async () => {
      try {
        const statusData = await checkConversionStatus(false);

        if (statusData?.isRunning === true) {
          shouldShowProgressRef.current = true;
          setConversionStatus(statusData);
          pollConversionStatus();
        }
      } catch (error) {
        console.log("INITIAL CONVERSION STATUS CHECK FAILED:", error);

        setConversionStatus(null);
      }
    };

    resumeRunningConversion();

    return () => {
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [qSetId]);

  useEffect(() => {
    let cancelled = false;

    const restoreQuestionConversion = async () => {
      if (!qSetId) {
        setConversionStatus(null);
        return;
      }

      try {
        const statusData = await checkConversionStatus(false);

        if (cancelled || !statusData) return;

        if (shouldContinuePolling(statusData)) {
          startConversionPolling();
        }
      } catch (error) {
        console.log("INITIAL QUESTION STATUS CHECK FAILED:", error);
      }
    };

    restoreQuestionConversion();

    return () => {
      cancelled = true;

      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }

      pollRequestInFlightRef.current = false;
      setPolling(false);
    };
  }, [qSetId]);

  useEffect(() => {
    if (selectedStream) {
      loadSubjects(selectedStream);
    } else {
      setSubjects([]);
    }

    setSubjectRows([createEmptySubjectRow()]);
  }, [selectedStream]);

  const loadStreams = async () => {
    try {
      const res = await apiService.request_old(
        "/i/ims/coption?option=STREAM&sort=seq%20ASC&status=true",
        { method: "GET" },
      );

      const rows = getRows(res);
      setStreams(rows.length ? rows : res || []);
    } catch (err) {
      console.log(err);
      showError("Stream load failed.");
    }
  };

  const loadSubjects = async (streamId) => {
    try {
      const res = await apiService.request_old(
        `/i/ims/subject?stream=${encodeURIComponent(streamId)}&limit=all&status=true`,
        {
          method: "GET",
        },
      );

      const rows = getRows(res);
      setSubjects(rows.length ? rows : res || []);
    } catch (err) {
      console.log(err);
      showError("Subject load failed.");
    }
  };

  const usedQuestions = useMemo(() => {
    return subjectRows.reduce((total, row) => {
      const start = Number(row.questionStart);
      const end = Number(row.questionEnd);

      if (!start || !end || end < start) return total;

      return total + (end - start + 1);
    }, 0);
  }, [subjectRows]);

  const canAddMore = totalQuestions > 0 && usedQuestions < totalQuestions;

  const updateRow = (index, key, value) => {
    setSubjectRows((prev) => {
      const rows = [...prev];
      const current = { ...rows[index] };

      current[key] = value;

      if (key === "subject") {
        const selectedSubject = subjects.find(
          (item) => String(item.id) === String(value),
        );

        const subjectName = getSubjectName(selectedSubject);
        const sectionKey = makeSectionKeyFromName(subjectName, index + 1);

        current.subject = value || "";
        current.subjectId = value || "";
        current.subjectName = subjectName;
        current.sectionName = subjectName;
        current.sectionKey = sectionKey;
      }

      rows[index] = current;
      return rows;
    });
  };

  const addRow = () => {
    if (!canAddMore) return;
    setSubjectRows((prev) => [...prev, createEmptySubjectRow()]);
  };

  const deleteRow = (index) => {
    if (subjectRows.length === 1) {
      showWarning("At least one subject row is required.");
      return;
    }

    setSubjectRows((prev) => prev.filter((_, i) => i !== index));
  };

  const validateSubjectRows = () => {
    if (!selectedStream) {
      showWarning("Please select stream.");
      return false;
    }

    const totalQ = Number(totalQuestions);

    if (!totalQ || totalQ <= 0) {
      showWarning("Please enter total questions.");
      return false;
    }

    const duplicateSectionMap = {};
    const usedQuestionMap = {};
    let usedCount = 0;

    for (let i = 0; i < subjectRows.length; i++) {
      const row = subjectRows[i];

      const start = Number(row.questionStart);
      const end = Number(row.questionEnd);

      if (!row.subject) {
        showWarning(`Please select subject in row ${i + 1}.`);
        return false;
      }

      if (!row.subjectName) {
        showWarning(`Subject name missing in row ${i + 1}.`);
        return false;
      }

      if (!row.sectionKey) {
        showWarning(`Section key missing in row ${i + 1}.`);
        return false;
      }

      if (!start || !end) {
        showWarning(`Please enter question start and end in row ${i + 1}.`);
        return false;
      }

      if (start < 1) {
        showWarning(`Question start should be greater than 0 in row ${i + 1}.`);
        return false;
      }

      if (end < start) {
        showWarning(
          `Question end should be greater than start in row ${i + 1}.`,
        );
        return false;
      }

      const sectionKey = row.sectionKey.toUpperCase();

      duplicateSectionMap[sectionKey] =
        (duplicateSectionMap[sectionKey] || 0) + 1;

      for (let qNo = start; qNo <= end; qNo++) {
        const questionKey = `${sectionKey}_${qNo}`;

        if (usedQuestionMap[questionKey]) {
          showWarning(
            `Question range overlaps in ${sectionKey} at question ${qNo}.`,
          );
          return false;
        }

        usedQuestionMap[questionKey] = true;
      }

      usedCount += end - start + 1;
    }

    const duplicateKeys = Object.keys(duplicateSectionMap).filter(
      (key) => duplicateSectionMap[key] > 1,
    );

    if (duplicateKeys.length > 0) {
      showWarning(`Duplicate subject found: ${duplicateKeys.join(", ")}`);
      return false;
    }

    if (usedCount !== totalQ) {
      showWarning(
        `Subject question range total must be exactly ${totalQ}. Current used: ${usedCount}.`,
      );
      return false;
    }

    return true;
  };

  const buildCleanSubjectRows = () => {
    return subjectRows.map((row, index) => {
      const subjectName = row.subjectName || row.sectionName || "";
      const sectionKey =
        row.sectionKey || makeSectionKeyFromName(subjectName, index + 1);

      return {
        sectionKey,
        sectionName: subjectName,
        subject: String(row.subject),
        subjectId: String(row.subject),
        subjectName,
        questionStart: Number(row.questionStart),
        questionEnd: Number(row.questionEnd),
      };
    });
  };

  const clearPollTimer = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const checkConversionStatus = async (
    showRunningWarning = false,
    options = {},
  ) => {
    if (!qSetId) {
      setConversionStatus(null);
      return null;
    }

    const checkRes = await apiService.request(
      `${CHECK_CONVERSION_API}?qSetId=${encodeURIComponent(
        qSetId,
      )}&type=Question`,
      {
        method: "GET",
      },
    );

    const statusData = getDataObject(checkRes);

    const activeStep = Number(statusData?.activeStep ?? 0);
    const uploadStatus = String(statusData?.uploadStatus || "");
    const isRunning = statusData?.isRunning === true;

    const isTerminal =
      activeStep >= 3 || activeStep < 0 || uploadStatus === "Failed";

    if (isRunning) {
      shouldShowProgressRef.current = true;
      setConversionStatus(statusData);
    } else if (shouldShowProgressRef.current || options.forceShow === true) {
      setConversionStatus(statusData);
    } else if (isTerminal) {
      setConversionStatus(null);
    } else {
      setConversionStatus(null);
    }

    if (showRunningWarning && isRunning) {
      showWarning(
        `A conversion is already running for this QSet. Current stage: ${
          statusData.message || getStageText(statusData.activeStep)
        }`,
      );
    }

    return statusData;
  };

  const scheduleNextPoll = () => {
    clearPollTimer();

    pollTimerRef.current = setTimeout(() => {
      pollConversionStatus();
    }, POLL_INTERVAL_MS);
  };

  const stopConversionPolling = () => {
    clearPollTimer();
    pollRequestInFlightRef.current = false;
    setPolling(false);
  };

  const showTerminalStatusToast = (statusData, terminalState) => {
    const toastKey = `${statusData?.jobId || qSetId}:${
      terminalState.failed ? "failed" : "completed"
    }`;

    if (terminalToastKeyRef.current === toastKey) {
      return;
    }

    terminalToastKeyRef.current = toastKey;

    if (terminalState.completed) {
      showSuccess(statusData?.message || "Conversion completed");
    } else if (terminalState.failed) {
      showError(statusData?.message || "Conversion failed");
    }
  };

  const pollConversionStatus = async () => {
    if (!qSetId || pollRequestInFlightRef.current) return;

    clearPollTimer();
    pollRequestInFlightRef.current = true;
    setPolling(true);

    try {
      const statusData = await checkConversionStatus(false);

      /*
       * Upload ke turant baad status record milne me thoda delay ho sakta hai.
       * Isliye empty status par bhi polling band nahi karte.
       */
      if (!statusData) {
        scheduleNextPoll();
        return;
      }

      const terminalState = getTerminalState(statusData);

      if (terminalState.terminal) {
        stopConversionPolling();
        showTerminalStatusToast(statusData, terminalState);
        return;
      }

      scheduleNextPoll();
    } catch (error) {
      /*
       * Temporary network/API error par polling stop nahi hogi.
       * Agli call automatically retry hogi.
       */
      console.error("QUESTION STATUS POLL ERROR:", error);
      scheduleNextPoll();
    } finally {
      pollRequestInFlightRef.current = false;
    }
  };

  const startConversionPolling = () => {
    terminalToastKeyRef.current = "";
    clearPollTimer();
    setPolling(true);
    pollConversionStatus();
  };

  const refreshConversionStatus = async () => {
    try {
      const statusData = await checkConversionStatus(false);

      if (statusData && shouldContinuePolling(statusData)) {
        startConversionPolling();
      }

      return statusData;
    } catch (error) {
      console.error("QUESTION STATUS REFRESH ERROR:", error);
      showError(error?.message || "Status refresh failed.");
      return null;
    }
  };

  const handleSubmitCustom = async (data) => {
    const file = data.file?.[0];

    if (!validateSubjectRows()) return;

    if (!file) {
      showWarning("Please select a PDF file.");
      return;
    }

    if (file.type && file.type !== "application/pdf") {
      showWarning("Only PDF file is allowed.");
      return;
    }

    if (!qSetId) {
      showError("QSet ID not found.");
      return;
    }

    try {
      setLoading(true);

      let statusData = null;

      try {
        statusData = await checkConversionStatus(false);
      } catch (checkErr) {
        console.log("CHECK CONVERSION API FAILED:", checkErr);
      }

      if (statusData?.isRunning) {
        showWarning(
          `A conversion is already running for this QSet. Current stage: ${
            statusData.message || getStageText(statusData.activeStep)
          }`,
        );
        startConversionPolling();
        return;
      }

      const sections = buildCleanSubjectRows();
      const queryParams = new URLSearchParams();

      queryParams.append("qSet", String(qSetId));
      queryParams.append("stream", String(data.stream));
      queryParams.append("totalQuestions", String(data.totalQuestions));
      queryParams.append("layoutType", String(data.layoutType || "AUTO"));
      queryParams.append(
        "translateMissingLanguage",
        data.translateMissingLanguage ? "true" : "false",
      );
      queryParams.append("sections", JSON.stringify(sections));
      queryParams.append("subjects", JSON.stringify(sections));

      const uploadUrl = `${createUploadApi(qSetId)}?${queryParams.toString()}`;
      const formData = new FormData();

      formData.append("pdfFile", file);
      formData.append("qSet", String(qSetId));
      formData.append("stream", String(data.stream));
      formData.append("totalQuestions", String(data.totalQuestions));
      formData.append("layoutType", String(data.layoutType || "AUTO"));
      formData.append(
        "translateMissingLanguage",
        data.translateMissingLanguage ? "true" : "false",
      );
      formData.append("sections", JSON.stringify(sections));
      formData.append("subjects", JSON.stringify(sections));

      const uploadRes = await apiService.request(uploadUrl, {
        method: "POST",
        body: formData,
      });

      const uploadData = getDataObject(uploadRes);

      if (uploadData?.alreadyRunning || uploadData?.isRunning) {
        shouldShowProgressRef.current = true;
        setConversionStatus(uploadData);

        showWarning(
          `A conversion is already running for this QSet. Current stage: ${
            uploadData.message || getStageText(uploadData.activeStep)
          }`,
        );

        pollConversionStatus();
        return;
      }
      const jobId = uploadData?.jobId || uploadRes?.jobId;

      shouldShowProgressRef.current = true;

      setConversionStatus({
        isRunning: true,
        jobId,
        activeStep: 0,
        uploadStatus: "Uploading",
        message: uploadData?.message || "PDF upload started",
        parts: uploadData?.parts || [],
        sections,
        layoutType: data.layoutType || "AUTO",
        translateMissingLanguage: Boolean(data.translateMissingLanguage),
      });

      pollConversionStatus();

      showInfo("PDF upload started.");
      showSuccess("File uploaded successfully. Conversion has started.");

      reset({
        layoutType: "AUTO",
        translateMissingLanguage: false,
      });
      setSubjectRows([createEmptySubjectRow()]);
      startConversionPolling();
    } catch (err) {
      console.log("UPLOAD ERROR:", err);
      showError(err?.message || "Upload failed.");
    } finally {
      setLoading(false);
    }
  };

  const progressPercent = getProgressPercent(conversionStatus?.activeStep);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Add Questions</h1>

        <Button
          onClick={() => navigate("/tools/qSet")}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </Button>
      </div>

      {conversionStatus && (
        <div
          className={`mb-4 rounded-lg border p-4 ${
            Number(conversionStatus?.activeStep) < 0
              ? "border-red-300 bg-red-50"
              : Number(conversionStatus?.activeStep) >= 3
                ? "border-green-300 bg-green-50"
                : "border-yellow-300 bg-yellow-50"
          }`}
        >
          <div className="flex justify-between items-start gap-3">
            <div>
              <p
                className={`font-semibold ${
                  Number(conversionStatus?.activeStep) < 0
                    ? "text-red-800"
                    : Number(conversionStatus?.activeStep) >= 3
                      ? "text-green-800"
                      : "text-yellow-800"
                }`}
              >
                {conversionStatus.message ||
                  getStageText(conversionStatus.activeStep)}
              </p>

              <p className="text-sm mt-1">
                Stage: {getStageText(conversionStatus.activeStep)}
              </p>

              {conversionStatus.jobId && (
                <p className="text-xs mt-1 break-all">
                  Job ID: {conversionStatus.jobId}
                </p>
              )}
            </div>

            <button
              type="button"
              onClick={refreshConversionStatus}
              className="px-3 py-1 rounded bg-gray-800 text-white text-sm"
            >
              Refresh
            </button>
          </div>

          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{progressPercent}%</span>
            </div>

            <div className="h-2 overflow-hidden rounded-full bg-gray-200">
              <div
                className={`h-full rounded-full ${
                  Number(conversionStatus?.activeStep) < 0
                    ? "bg-red-500"
                    : Number(conversionStatus?.activeStep) >= 3
                      ? "bg-green-500"
                      : "bg-blue-500"
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          {polling && (
            <p className="text-xs mt-2 text-blue-700">
              Status auto refresh is running...
            </p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
        <div className="p-4">
          <form onSubmit={handleSubmit(handleSubmitCustom)}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-3">
              <div>
                <label className="block text-gray-700 mb-2">
                  Stream <span className="text-red-500">*</span>
                </label>

                <select
                  className="w-full border rounded p-2"
                  disabled={loading}
                  {...register("stream", {
                    required: "Stream is required",
                  })}
                >
                  <option value="">Select Stream</option>

                  {streams.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.value || item.valueAlias || item.name || item.code}
                      {item.code ? ` (${item.code})` : ""}
                    </option>
                  ))}
                </select>

                {errors.stream && (
                  <p className="text-red-500 text-sm">
                    {errors.stream.message}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-700 mb-2">
                  Total Questions <span className="text-red-500">*</span>
                </label>

                <input
                  type="number"
                  min="1"
                  className="w-full border rounded p-2"
                  disabled={loading || !selectedStream}
                  placeholder="Enter total questions"
                  {...register("totalQuestions", {
                    required: "Total questions is required",
                    min: {
                      value: 1,
                      message: "Minimum 1 question required",
                    },
                  })}
                />

                {errors.totalQuestions && (
                  <p className="text-red-500 text-sm">
                    {errors.totalQuestions.message}
                  </p>
                )}
              </div>
            </div>

            {selectedStream && totalQuestions > 0 && (
              <div className="p-3">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="font-semibold">Subject Question Range</h3>
                  </div>

                  <span
                    className={`text-sm ${
                      usedQuestions === totalQuestions
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    Used: {usedQuestions} / {totalQuestions}
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full border text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="border p-2 text-left">Subject</th>
                        <th className="border p-2 text-left">Question Start</th>
                        <th className="border p-2 text-left">Question End</th>
                        <th className="border p-2 text-center">Count</th>
                        <th className="border p-2 text-center">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {subjectRows.map((row, index) => (
                        <tr key={index}>
                          <td className="border p-2 min-w-[220px]">
                            <select
                              className="w-full border rounded p-2"
                              value={row.subject}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(index, "subject", e.target.value)
                              }
                            >
                              <option value="">Select Subject</option>

                              {subjects.map((item) => (
                                <option key={item.id} value={item.id}>
                                  {getSubjectName(item)}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td className="border p-2 min-w-[130px]">
                            <input
                              type="number"
                              min="1"
                              className="w-full border rounded p-2"
                              value={row.questionStart}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(
                                  index,
                                  "questionStart",
                                  e.target.value,
                                )
                              }
                            />
                          </td>

                          <td className="border p-2 min-w-[130px]">
                            <input
                              type="number"
                              min="1"
                              className="w-full border rounded p-2"
                              value={row.questionEnd}
                              disabled={loading}
                              onChange={(e) =>
                                updateRow(index, "questionEnd", e.target.value)
                              }
                            />
                          </td>

                          <td className="border p-2 text-center font-semibold">
                            {Number(row.questionStart) &&
                            Number(row.questionEnd) &&
                            Number(row.questionEnd) >= Number(row.questionStart)
                              ? Number(row.questionEnd) -
                                Number(row.questionStart) +
                                1
                              : 0}
                          </td>

                          <td className="border p-2 text-center">
                            <button
                              type="button"
                              disabled={loading || subjectRows.length === 1}
                              onClick={() => deleteRow(index)}
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded text-white ${
                                subjectRows.length === 1
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-red-500 hover:bg-red-600"
                              }`}
                            >
                              <FaTrash className="h-3 w-3" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <button
                  type="button"
                  disabled={!canAddMore || loading}
                  onClick={addRow}
                  className={`mt-3 inline-flex items-center gap-2 px-4 py-2 rounded text-white ${
                    !canAddMore || loading
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-500 hover:bg-blue-600"
                  }`}
                >
                  <FaPlus className="h-3 w-3" />
                  Add More
                </button>
              </div>
            )}

            <div className="p-3">
              <label className="block text-gray-700 mb-2">
                PDF Layout / Format <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full md:w-1/2 border rounded p-2"
                disabled={loading}
                {...register("layoutType", { required: true })}
              >
                <option value="AUTO">Auto detect (recommended)</option>
                <option value="BILINGUAL_TWO_COLUMN">
                  English left + Hindi right
                </option>
                <option value="SINGLE_COLUMN">Single column</option>
                {/* <option value="MULTI_COLUMN">Multiple independent columns</option> */}
                <option value="QUESTION_GRID">
                  Question grid (1-2-3 / 4-5-6)
                </option>
                {/* <option value="SCANNED_IMAGE">Scanned / image PDF</option> */}
              </select>
              {/* <p className="text-xs text-gray-500 mt-1">
                Current: {selectedLayoutType}. AUTO works for most PDFs; choose
                an exact type for difficult layouts.
              </p> */}
            </div>

            <div className="p-3">
              <label className="block text-gray-700 mb-2">
                Missing Language Translation
              </label>

              <label className="inline-flex items-start gap-3 rounded border p-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4"
                  disabled={loading}
                  {...register("translateMissingLanguage")}
                />
                <span>
                  <span className="block font-medium">
                    Translate when PDF has only one language
                  </span>
                  {/* <span className="block text-xs text-gray-500 mt-1">
                    Checked: English-only PDF also creates Hindi, and Hindi-only
                    PDF also creates English. Unchecked: only the language
                    actually present in the PDF is saved. A bilingual PDF always
                    keeps both original languages.
                  </span> */}
                </span>
              </label>

              {/* <p className="text-xs text-gray-500 mt-2">
                Current:{" "}
                {translateMissingLanguage
                  ? "Translation enabled"
                  : "Translation disabled"}
              </p> */}
            </div>

            <div className="p-3">
              <label className="block text-gray-700 mb-2">
                Select PDF File <span className="text-red-500">*</span>
              </label>

              <input
                type="file"
                className="m-2"
                accept=".pdf,application/pdf"
                disabled={loading || !selectedStream || !totalQuestions}
                {...register("file", {
                  required: "File is required",
                })}
              />

              {errors.file && (
                <p className="text-red-500 text-sm">{errors.file.message}</p>
              )}
            </div>

            <div className="p-3 flex items-center gap-3">
              <button
                type="submit"
                disabled={loading}
                className={`font-thin text-white p-2 rounded m-2 ${
                  loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-green-500 hover:bg-green-600"
                }`}
              >
                {loading ? "Checking..." : "Upload"}
              </button>

              {loading && <Loader />}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadQuestionNew;
